/**
 * Contribution Service
 * Manages contributions, donations, tithes, offerings
 * Handles manual and digital contributions
 */

import { supabase } from '@/lib/supabase'
import type { Contribution } from './financeService'

// =====================================================
// CONTRIBUTION SERVICE
// =====================================================

export class ContributionService {
  /**
   * Get contributions with filters
   */
  static async getContributions(
    churchId: string,
    filters?: {
      memberId?: string
      contributionType?: string
      startDate?: string
      endDate?: string
      fundId?: string
      status?: string
      search?: string
      limit?: number
      offset?: number
    }
  ): Promise<{ data: Contribution[]; count: number }> {
    let query = supabase
      .from('contributions')
      .select(`
        *,
        fund:funds(*),
        member:members(
          id,
          profile:profiles(id, first_name, last_name, email, phone)
        )
      `, { count: 'exact' })
      .eq('church_id', churchId)
      .order('contribution_date', { ascending: false })

    if (filters?.memberId) {
      query = query.eq('member_id', filters.memberId)
    }

    if (filters?.contributionType) {
      query = query.eq('contribution_type', filters.contributionType)
    }

    if (filters?.startDate) {
      query = query.gte('contribution_date', filters.startDate)
    }

    if (filters?.endDate) {
      query = query.lte('contribution_date', filters.endDate)
    }

    if (filters?.fundId) {
      query = query.eq('fund_id', filters.fundId)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.search) {
      query = query.or(`contributor_name.ilike.%${filters.search}%,reference_number.ilike.%${filters.search}%`)
    }

    if (filters?.limit) {
      query = query.limit(filters.limit)
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
    }

    const { data, error, count } = await query

    if (error) throw error
    return { data: data || [], count: count || 0 }
  }

  /**
   * Get contribution by ID
   */
  static async getContributionById(contributionId: string): Promise<Contribution | null> {
    const { data, error } = await supabase
      .from('contributions')
      .select(`
        *,
        fund:funds(*),
        member:members(
          id,
          profile:profiles(id, first_name, last_name, email, phone)
        ),
        journal_entry:general_ledger(*)
      `)
      .eq('id', contributionId)
      .single()

    if (error) throw error
    return data
  }

  /**
   * Create contribution (manual entry)
   */
  static async createContribution(
    contribution: Partial<Contribution>,
    recordedBy: string
  ): Promise<Contribution> {
    const { data, error } = await supabase
      .from('contributions')
      .insert([{
        ...contribution,
        status: contribution.status || 'completed',
        recorded_by: recordedBy,
        created_by: recordedBy
      }])
      .select()
      .single()

    if (error) throw error

    // The trigger will automatically create the journal entry
    return data
  }

  /**
   * Update contribution (only if not posted to ledger)
   */
  static async updateContribution(
    contributionId: string,
    updates: Partial<Contribution>
  ): Promise<Contribution> {
    // Check if already posted
    const existing = await this.getContributionById(contributionId)
    if (existing?.is_posted_to_ledger) {
      throw new Error('Cannot modify contributions that have been posted to the ledger')
    }

    const { data, error } = await supabase
      .from('contributions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', contributionId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Delete contribution (only if not posted)
   */
  static async deleteContribution(contributionId: string): Promise<void> {
    const existing = await this.getContributionById(contributionId)
    if (existing?.is_posted_to_ledger) {
      throw new Error('Cannot delete contributions that have been posted to the ledger')
    }

    const { error } = await supabase
      .from('contributions')
      .delete()
      .eq('id', contributionId)

    if (error) throw error
  }

  /**
   * Reverse a contribution (proper accounting method)
   * Changes status to 'voided' which triggers the reversal trigger
   * The trigger automatically creates reversing journal entries
   */
  static async reverseContribution(
    contributionId: string,
    reversedBy: string,
    reason: string
  ): Promise<Contribution> {
    const original = await this.getContributionById(contributionId)
    if (!original) {
      throw new Error('Original contribution not found')
    }

    if (original.status === 'voided') {
      throw new Error('This contribution has already been reversed')
    }

    if (!original.is_posted_to_ledger) {
      throw new Error('Cannot reverse a contribution that has not been posted to the ledger')
    }

    // Mark as voided - this triggers the reversal trigger automatically
    const { data, error } = await supabase
      .from('contributions')
      .update({
        status: 'voided',
        notes: `${original.notes ? original.notes + '\n\n' : ''}VOIDED on ${new Date().toISOString()}. Reversed by: ${reversedBy}. Reason: ${reason}`,
        updated_at: new Date().toISOString(),
        updated_by: reversedBy
      })
      .eq('id', contributionId)
      .select()
      .single()

    if (error) throw error

    return data
  }

  /**
   * Get contribution summary by member
   */
  static async getMemberContributionSummary(
    churchId: string,
    memberId: string,
    fiscalYear?: number
  ): Promise<{
    total_amount: number
    total_count: number
    by_type: Record<string, { amount: number; count: number }>
    by_month: Record<string, { amount: number; count: number }>
  }> {
    let query = supabase
      .from('contributions')
      .select('contribution_type, contribution_date, amount')
      .eq('church_id', churchId)
      .eq('member_id', memberId)
      .eq('status', 'completed')

    if (fiscalYear) {
      query = query
        .gte('contribution_date', `${fiscalYear}-01-01`)
        .lte('contribution_date', `${fiscalYear}-12-31`)
    }

    const { data, error } = await query

    if (error) throw error

    const summary = {
      total_amount: 0,
      total_count: 0,
      by_type: {} as Record<string, { amount: number; count: number }>,
      by_month: {} as Record<string, { amount: number; count: number }>
    }

    data?.forEach(contrib => {
      // Total
      summary.total_amount += contrib.amount
      summary.total_count += 1

      // By type
      if (!summary.by_type[contrib.contribution_type]) {
        summary.by_type[contrib.contribution_type] = { amount: 0, count: 0 }
      }
      summary.by_type[contrib.contribution_type].amount += contrib.amount
      summary.by_type[contrib.contribution_type].count += 1

      // By month
      const month = contrib.contribution_date.substring(0, 7) // YYYY-MM
      if (!summary.by_month[month]) {
        summary.by_month[month] = { amount: 0, count: 0 }
      }
      summary.by_month[month].amount += contrib.amount
      summary.by_month[month].count += 1
    })

    return summary
  }

  /**
   * Get contribution summary for church (dashboard)
   */
  static async getChurchContributionSummary(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<{
    total_amount: number
    total_count: number
    by_type: Record<string, { amount: number; count: number }>
    by_fund: Record<string, { amount: number; count: number }>
    top_contributors: Array<{ member_id: string; member_name: string; total_amount: number }>
  }> {
    const { data, error } = await supabase
      .from('contributions')
      .select(`
        *,
        member:members(
          id,
          profile:profiles(id, first_name, last_name)
        ),
        fund:funds(fund_name)
      `)
      .eq('church_id', churchId)
      .eq('status', 'completed')
      .gte('contribution_date', startDate)
      .lte('contribution_date', endDate)

    if (error) throw error

    const summary = {
      total_amount: 0,
      total_count: 0,
      by_type: {} as Record<string, { amount: number; count: number }>,
      by_fund: {} as Record<string, { amount: number; count: number }>,
      top_contributors: [] as Array<{ member_id: string; member_name: string; total_amount: number }>
    }

    const memberTotals: Record<string, { name: string; amount: number }> = {}

    data?.forEach(contrib => {
      // Total
      summary.total_amount += contrib.amount
      summary.total_count += 1

      // By type
      if (!summary.by_type[contrib.contribution_type]) {
        summary.by_type[contrib.contribution_type] = { amount: 0, count: 0 }
      }
      summary.by_type[contrib.contribution_type].amount += contrib.amount
      summary.by_type[contrib.contribution_type].count += 1

      // By fund
      const fundName = contrib.fund?.fund_name || 'General'
      if (!summary.by_fund[fundName]) {
        summary.by_fund[fundName] = { amount: 0, count: 0 }
      }
      summary.by_fund[fundName].amount += contrib.amount
      summary.by_fund[fundName].count += 1

      // Track member totals
      if (contrib.member_id && contrib.member && contrib.member.profile) {
        if (!memberTotals[contrib.member_id]) {
          memberTotals[contrib.member_id] = {
            name: `${contrib.member.profile.first_name} ${contrib.member.profile.last_name}`,
            amount: 0
          }
        }
        memberTotals[contrib.member_id].amount += contrib.amount
      }
    })

    // Top contributors
    summary.top_contributors = Object.entries(memberTotals)
      .map(([memberId, { name, amount }]) => ({
        member_id: memberId,
        member_name: name,
        total_amount: amount
      }))
      .sort((a, b) => b.total_amount - a.total_amount)
      .slice(0, 10)

    return summary
  }

  /**
   * Generate tax receipt for contributions
   */
  static async generateTaxReceipt(
    memberId: string,
    taxYear: number
  ): Promise<{
    receipt_number: string
    total_amount: number
    contributions: Contribution[]
  }> {
    // Get all tax-deductible contributions for the year
    const { data, error } = await supabase
      .from('contributions')
      .select('*')
      .eq('member_id', memberId)
      .eq('status', 'completed')
      .eq('is_tax_deductible', true)
      .gte('contribution_date', `${taxYear}-01-01`)
      .lte('contribution_date', `${taxYear}-12-31`)
      .order('contribution_date')

    if (error) throw error

    const totalAmount = data?.reduce((sum, c) => sum + c.amount, 0) || 0

    // Generate receipt number
    const receiptNumber = `TR-${taxYear}-${memberId.substring(0, 8).toUpperCase()}`

    return {
      receipt_number: receiptNumber,
      total_amount: totalAmount,
      contributions: data || []
    }
  }

  /**
   * Bulk import contributions from CSV/Excel
   */
  static async bulkImportContributions(
    churchId: string,
    contributions: Partial<Contribution>[],
    importedBy: string
  ): Promise<{
    success: number
    failed: number
    errors: Array<{ row: number; error: string }>
  }> {
    const results = {
      success: 0,
      failed: 0,
      errors: [] as Array<{ row: number; error: string }>
    }

    for (let i = 0; i < contributions.length; i++) {
      try {
        await this.createContribution(
          { ...contributions[i], church_id: churchId },
          importedBy
        )
        results.success++
      } catch (error: any) {
        results.failed++
        results.errors.push({
          row: i + 1,
          error: error.message
        })
      }
    }

    return results
  }
}

export default ContributionService
